/**
 * Admin Website — Pages Manager (list + editor).
 * List: table of every page (marketing/system/dashboard) with filters.
 * Editor: opens a page in the visual builder with a settings panel.
 *
 * The visual builder uses @dnd-kit for section + block reordering with
 * up/down buttons as the accessible alternative (spec requirement).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowUp,
  ArrowDown,
  Copy,
  Trash2,
  Plus,
  Eye,
  Save,
  History,
  Sparkles,
  Layers,
  Undo2,
  Redo2,
  Smartphone,
  Tablet,
  Monitor,
  Lock,
  Palette,
  Search,
  ExternalLink,
  Settings2,
} from "lucide-react";
import api from "@/lib/api";
import { BLOCK_REGISTRY, SectionRenderer, blockDefaults, isBlockProtected } from "@/builder/blockRegistry";

const uid = (p = "b") => `${p}_${Math.random().toString(36).slice(2, 9)}`;
const cx = (...c) => c.filter(Boolean).join(" ");

// =================================================================
// Pages LIST
// =================================================================
export function AdminWebsitePage() {
  const nav = useNavigate();
  const [pages, setPages] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/admin/pages", { params: { q: q || undefined, page_type: typeFilter || undefined, status: statusFilter || undefined, limit: 500 } });
    setPages(data.items || []);
    setLoading(false);
  }, [q, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const syncRoutes = async () => {
    const { data } = await api.post("/admin/pages/sync-routes");
    setSyncMsg(`Registered ${data.added} new routes, updated ${data.updated} existing.`);
    await load();
    toast.success("Route registry synced");
  };

  const publish = async (p) => {
    await api.post(`/admin/pages/${p.id}/publish`, {});
    toast.success("Published");
    load();
  };
  const unpublish = async (p) => {
    await api.post(`/admin/pages/${p.id}/unpublish`);
    toast.success("Unpublished");
    load();
  };
  const archive = async (p) => {
    if (!window.confirm("Archive this page?")) return;
    try {
      await api.post(`/admin/pages/${p.id}/archive`);
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Cannot archive");
    }
  };
  const duplicate = async (p) => {
    const { data } = await api.post(`/admin/pages/${p.id}/duplicate`);
    toast.success("Duplicated");
    nav(`/admin/website/${data.id}`);
  };

  const grouped = useMemo(() => {
    const groups = { static: [], "dynamic-template": [], system: [], dashboard: [] };
    pages.forEach(p => (groups[p.page_type] || (groups[p.page_type] = [])).push(p));
    return groups;
  }, [pages]);

  return (
    <div className="max-w-6xl" data-testid="admin-website">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Website</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Pages Manager</h1>
          <p className="text-[#252A34]/70 text-sm mt-1">Every route in the app is registered as a page. Static + template pages are visually editable — system and dashboard pages are code-rendered but their SEO metadata is still yours to control.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={syncRoutes} className="rounded-full border border-slate-200 bg-white text-[#165a71] px-4 py-2 text-sm font-medium hover:border-[#165a71]/40" data-testid="sync-routes-btn">
            <Layers className="h-3.5 w-3.5 inline -mt-0.5 mr-1.5" />Sync routes
          </button>
          <Link to="/admin/website/new" className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]" data-testid="new-page-btn">
            <Plus className="h-3.5 w-3.5 inline -mt-0.5 mr-1.5" />New page
          </Link>
        </div>
      </div>

      {syncMsg && <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm px-4 py-2">{syncMsg}</div>}

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title/slug/route" className="w-full pl-9 pr-3 py-2 text-sm rounded-full border border-slate-200 focus:border-[#165a71] focus:outline-none" data-testid="pages-search" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="rounded-full border border-slate-200 px-3 py-2 text-sm focus:outline-none" data-testid="pages-type-filter">
          <option value="">All types</option>
          <option value="static">Static</option>
          <option value="dynamic-template">Template</option>
          <option value="system">System</option>
          <option value="dashboard">Dashboard</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="rounded-full border border-slate-200 px-3 py-2 text-sm focus:outline-none" data-testid="pages-status-filter">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="scheduled">Scheduled</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {loading && <div className="text-sm text-[#252A34]/60">Loading…</div>}

      {["static", "dynamic-template", "system", "dashboard"].map(gkey => (
        (grouped[gkey] || []).length > 0 && (
          <div key={gkey} className="mb-8">
            <h2 className="font-display text-lg font-semibold text-[#071b22] mb-3">
              {gkey === "static" ? "Content pages"
                : gkey === "dynamic-template" ? "Dynamic templates"
                : gkey === "system" ? "System pages"
                : "Dashboard pages"} <span className="text-xs text-[#252A34]/60 font-normal">({(grouped[gkey] || []).length})</span>
            </h2>
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[#252A34]/70">
                  <tr>
                    <th className="text-left px-4 py-2">Title / Route</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Last edited</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(grouped[gkey] || []).map(p => (
                    <tr key={p.id} data-testid={`page-row-${p.slug}`} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#071b22]">{p.title}{p.is_home && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#165a71]">Home</span>}{p.review_by_counsel && <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-600">Legal</span>}</div>
                        <div className="text-xs text-[#252A34]/60 font-mono">{p.route}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cx("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", p.status === "published" ? "bg-emerald-100 text-emerald-800" : p.status === "draft" ? "bg-slate-100 text-slate-600" : p.status === "scheduled" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500")}>{p.status}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#252A34]/60">{p.last_editor_name || "—"} · {(p.updated_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {p.status !== "published" && !p.is_system && !p.is_dashboard && (
                            <button onClick={() => publish(p)} className="rounded px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50" data-testid={`publish-${p.slug}`}>Publish</button>
                          )}
                          {p.status === "published" && !p.is_system && !p.is_dashboard && (
                            <button onClick={() => unpublish(p)} className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100">Unpublish</button>
                          )}
                          <button onClick={() => duplicate(p)} className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100" title="Duplicate"><Copy className="h-3 w-3" /></button>
                          {!p.is_system && !p.is_dashboard && (
                            <button onClick={() => archive(p)} className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100" title="Archive"><Trash2 className="h-3 w-3" /></button>
                          )}
                          {p.is_dashboard || (p.is_system && !p.is_home) ? (
                            <Link to={`/admin/website/${p.id}?settings`} className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100" title="Settings only (code-rendered)"><Settings2 className="h-3 w-3" /></Link>
                          ) : (
                            <Link to={`/admin/website/${p.id}`} className="rounded bg-[#165a71] text-white px-3 py-1 text-xs font-medium hover:bg-[#11485a]" data-testid={`edit-${p.slug}`}>Edit visually</Link>
                          )}
                          {p.status === "published" && !p.is_dashboard && (
                            <a href={p.slug === "home" ? "/home-marketplace" : p.route} target="_blank" rel="noreferrer" className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100" title="Preview"><ExternalLink className="h-3 w-3" /></a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ))}
    </div>
  );
}

// =================================================================
// Editor
// =================================================================
export function AdminPageEditorPage() {
  const { pageId } = useParams();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [device, setDevice] = useState("desktop");   // desktop | tablet | mobile
  const [showSettings, setShowSettings] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);

  const load = useCallback(async () => {
    if (pageId === "new") { setPage({ new: true, sections: [] }); return; }
    const { data } = await api.get(`/admin/pages/${pageId}`);
    setPage(data);
    setSections(data.draft_sections?.length ? data.draft_sections : (data.sections || []));
  }, [pageId]);

  useEffect(() => { load(); }, [load]);

  const pushHistory = useCallback((next) => {
    setHistory((h) => {
      const truncated = h.slice(0, historyIdx + 1);
      const nextArr = [...truncated, JSON.parse(JSON.stringify(next))].slice(-30);
      return nextArr;
    });
    setHistoryIdx((i) => Math.min(i + 1, 29));
  }, [historyIdx]);

  const updateSections = (next, opts = {}) => {
    if (!opts.skipHistory) pushHistory(next);
    setSections(next);
    setDirty(true);
  };

  const undo = () => {
    if (historyIdx <= 0) return;
    const prev = history[historyIdx - 1];
    setSections(JSON.parse(JSON.stringify(prev)));
    setHistoryIdx(historyIdx - 1);
    setDirty(true);
  };
  const redo = () => {
    if (historyIdx >= history.length - 1) return;
    const next = history[historyIdx + 1];
    setSections(JSON.parse(JSON.stringify(next)));
    setHistoryIdx(historyIdx + 1);
    setDirty(true);
  };

  // Autosave every 20s while dirty
  useEffect(() => {
    if (!dirty || !page || page.new) return;
    const t = setTimeout(async () => {
      try {
        await api.put(`/admin/pages/${page.id}/content`, { sections, autosave: true });
        setDirty(false);
      } catch {}
    }, 20000);
    return () => clearTimeout(t);
  }, [dirty, sections, page]);

  const save = async (autosave = false) => {
    if (!page || page.new) return;
    setSaving(true);
    await api.put(`/admin/pages/${page.id}/content`, { sections, autosave, label: "manual save" });
    setSaving(false);
    setDirty(false);
    if (!autosave) toast.success("Draft saved");
  };

  const publish = async () => {
    if (!page || page.new) return;
    await api.put(`/admin/pages/${page.id}/content`, { sections });
    const { data } = await api.post(`/admin/pages/${page.id}/publish`, {});
    toast.success(`Published v${data.version}`);
    setDirty(false);
    load();
  };

  // Section handlers
  const addSection = () => {
    const newSec = { id: uid("sec"), layout: "container", name: "New section", blocks: [] };
    updateSections([...sections, newSec]);
    setSelectedId(newSec.id);
  };
  const removeSection = (id) => {
    updateSections(sections.filter(s => s.id !== id));
    if (selectedId === id) setSelectedId(null);
  };
  const moveSection = (id, delta) => {
    const idx = sections.findIndex(s => s.id === id);
    if (idx < 0) return;
    const to = idx + delta;
    if (to < 0 || to >= sections.length) return;
    const next = [...sections];
    const [item] = next.splice(idx, 1);
    next.splice(to, 0, item);
    updateSections(next);
  };
  const duplicateSection = (id) => {
    const idx = sections.findIndex(s => s.id === id);
    if (idx < 0) return;
    const cloned = JSON.parse(JSON.stringify(sections[idx]));
    cloned.id = uid("sec");
    cloned.blocks = (cloned.blocks || []).map(b => ({ ...b, id: uid("b") }));
    const next = [...sections];
    next.splice(idx + 1, 0, cloned);
    updateSections(next);
  };
  const toggleSectionVisibility = (id) => {
    const next = sections.map(s => s.id === id ? { ...s, hidden: !s.hidden } : s);
    updateSections(next);
  };

  const updateBlock = (sectionId, blockId, patch) => {
    const next = sections.map(s => s.id === sectionId
      ? { ...s, blocks: s.blocks.map(b => b.id === blockId ? { ...b, ...patch, props: { ...(b.props || {}), ...(patch.props || {}) } } : b) }
      : s);
    updateSections(next);
  };
  const updateSectionMeta = (sectionId, patch) => {
    const next = sections.map(s => s.id === sectionId ? { ...s, ...patch } : s);
    updateSections(next);
  };
  const addBlock = (sectionId, kind) => {
    const b = { id: uid("b"), kind, props: blockDefaults(kind), style: {}, responsive: {} };
    const next = sections.map(s => s.id === sectionId ? { ...s, blocks: [...(s.blocks || []), b] } : s);
    updateSections(next);
    setSelectedId(b.id);
  };
  const removeBlock = (sectionId, blockId) => {
    const s = sections.find(x => x.id === sectionId);
    if (!s) return;
    const blk = s.blocks.find(b => b.id === blockId);
    if (isBlockProtected(blk)) {
      if (!window.confirm("This is a protected system block. Removing it may break the page. Continue?")) return;
    }
    const next = sections.map(x => x.id === sectionId ? { ...x, blocks: x.blocks.filter(b => b.id !== blockId) } : x);
    updateSections(next);
  };
  const moveBlock = (sectionId, blockId, delta) => {
    const s = sections.find(x => x.id === sectionId);
    if (!s) return;
    const idx = s.blocks.findIndex(b => b.id === blockId);
    const to = idx + delta;
    if (idx < 0 || to < 0 || to >= s.blocks.length) return;
    const nextBlocks = [...s.blocks];
    const [item] = nextBlocks.splice(idx, 1);
    nextBlocks.splice(to, 0, item);
    const next = sections.map(x => x.id === sectionId ? { ...x, blocks: nextBlocks } : x);
    updateSections(next);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    // Sections
    if (String(active.id).startsWith("sec_") && String(over.id).startsWith("sec_")) {
      const oldIndex = sections.findIndex(s => s.id === active.id);
      const newIndex = sections.findIndex(s => s.id === over.id);
      if (oldIndex >= 0 && newIndex >= 0) updateSections(arrayMove(sections, oldIndex, newIndex));
      return;
    }
  };

  if (!page) return <div className="max-w-6xl mx-auto py-24 px-4 animate-pulse text-[#252A34]/60">Loading editor…</div>;

  if (page.new) return <NewPageDialog onCreated={(p) => nav(`/admin/website/${p.id}`)} />;

  const selectedBlock = (() => {
    for (const s of sections) {
      const b = (s.blocks || []).find(x => x.id === selectedId);
      if (b) return { section: s, block: b };
    }
    return null;
  })();
  const selectedSection = sections.find(s => s.id === selectedId);

  const deviceWidth = device === "mobile" ? "max-w-[420px]" : device === "tablet" ? "max-w-[820px]" : "max-w-6xl";

  return (
    <div className="min-h-screen bg-slate-50 -m-6" data-testid="page-editor">
      {/* Top bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/admin/website" className="text-xs text-[#165a71] font-medium hover:underline">← Pages</Link>
          <div className="min-w-0">
            <div className="font-display font-semibold text-[#071b22] truncate">{page.title}</div>
            <div className="text-xs text-[#252A34]/60 font-mono truncate">{page.route}</div>
          </div>
          {dirty && <span className="text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-medium">Unsaved</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={undo} disabled={historyIdx <= 0} className="rounded p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-40" title="Undo" data-testid="undo-btn"><Undo2 className="h-4 w-4" /></button>
          <button onClick={redo} disabled={historyIdx >= history.length - 1} className="rounded p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-40" title="Redo" data-testid="redo-btn"><Redo2 className="h-4 w-4" /></button>
          <div className="mx-2 h-6 w-px bg-slate-200" />
          {[
            ["desktop", Monitor, "Desktop"],
            ["tablet", Tablet, "Tablet"],
            ["mobile", Smartphone, "Mobile"],
          ].map(([k, Icon, label]) => (
            <button key={k} onClick={() => setDevice(k)} className={cx("rounded p-2 text-slate-500 hover:bg-slate-100", device === k && "bg-slate-100 text-[#165a71]")} title={label} data-testid={`device-${k}`}>
              <Icon className="h-4 w-4" />
            </button>
          ))}
          <div className="mx-2 h-6 w-px bg-slate-200" />
          <button onClick={() => setShowRevisions(true)} className="rounded p-2 text-slate-500 hover:bg-slate-100" title="Revisions" data-testid="revisions-btn"><History className="h-4 w-4" /></button>
          <button onClick={() => setShowSettings(true)} className="rounded p-2 text-slate-500 hover:bg-slate-100" title="Page settings" data-testid="settings-btn"><Settings2 className="h-4 w-4" /></button>
          <a href={page.route} target="_blank" rel="noreferrer" className="rounded p-2 text-slate-500 hover:bg-slate-100" title="Preview"><Eye className="h-4 w-4" /></a>
          <button onClick={() => save(false)} disabled={saving || !dirty} className="ml-2 rounded-full border border-slate-200 bg-white text-[#165a71] px-4 py-1.5 text-sm font-medium hover:border-[#165a71]/40 disabled:opacity-50" data-testid="save-draft-btn"><Save className="h-3.5 w-3.5 inline -mt-0.5 mr-1.5" />Save draft</button>
          <button onClick={publish} className="rounded-full bg-[#165a71] text-white px-4 py-1.5 text-sm font-medium hover:bg-[#11485a]" data-testid="publish-btn"><Sparkles className="h-3.5 w-3.5 inline -mt-0.5 mr-1.5" />Publish</button>
        </div>
      </header>

      <div className="flex">
        {/* Left sidebar — Add blocks */}
        <aside className="w-64 shrink-0 border-r border-slate-200 bg-white p-3 min-h-[calc(100vh-3.5rem)] max-h-[calc(100vh-3.5rem)] overflow-auto sticky top-14">
          <div className="text-xs uppercase font-semibold tracking-wider text-[#252A34]/60 mb-3">Add block</div>
          {Object.entries(groupedBlocks()).map(([group, blocks]) => (
            <div key={group} className="mb-4">
              <div className="text-xs font-semibold text-[#071b22] mb-1.5 capitalize">{group}</div>
              <div className="grid grid-cols-2 gap-1.5">
                {blocks.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => {
                      const target = selectedSection?.id || sections[0]?.id;
                      if (!target) { toast.error("Add a section first"); return; }
                      addBlock(target, key);
                    }}
                    className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-left hover:border-[#165a71]/40 hover:bg-[#165a71]/5"
                    data-testid={`add-block-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>

        {/* Canvas */}
        <main className="flex-1 py-6 px-4">
          <div className={cx("mx-auto transition-all", deviceWidth)}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                {sections.map((s, idx) => (
                  <SortableSection
                    key={s.id}
                    section={s}
                    selectedId={selectedId}
                    setSelectedId={setSelectedId}
                    onMoveUp={() => moveSection(s.id, -1)}
                    onMoveDown={() => moveSection(s.id, 1)}
                    onDuplicate={() => duplicateSection(s.id)}
                    onRemove={() => removeSection(s.id)}
                    onToggleVisibility={() => toggleSectionVisibility(s.id)}
                    onBlockMoveUp={(bid) => moveBlock(s.id, bid, -1)}
                    onBlockMoveDown={(bid) => moveBlock(s.id, bid, 1)}
                    onBlockRemove={(bid) => removeBlock(s.id, bid)}
                    idx={idx}
                    total={sections.length}
                  />
                ))}
              </SortableContext>
            </DndContext>

            <button onClick={addSection} className="mt-4 w-full rounded-2xl border-2 border-dashed border-slate-300 bg-white/60 py-6 text-sm text-[#165a71] hover:border-[#165a71] hover:bg-[#165a71]/5" data-testid="add-section-btn">
              <Plus className="h-4 w-4 inline -mt-0.5 mr-1.5" />Add section
            </button>
          </div>
        </main>

        {/* Right settings panel */}
        <aside className="w-72 shrink-0 border-l border-slate-200 bg-white p-4 min-h-[calc(100vh-3.5rem)] max-h-[calc(100vh-3.5rem)] overflow-auto sticky top-14">
          <div className="text-xs uppercase font-semibold tracking-wider text-[#252A34]/60 mb-3">Settings</div>
          {selectedBlock ? (
            <BlockSettingsPanel entry={selectedBlock} onChange={(patch) => updateBlock(selectedBlock.section.id, selectedBlock.block.id, patch)} device={device} />
          ) : selectedSection ? (
            <SectionSettingsPanel section={selectedSection} onChange={(patch) => updateSectionMeta(selectedSection.id, patch)} />
          ) : (
            <p className="text-xs text-[#252A34]/60">Click a section or block to edit its settings here.</p>
          )}
        </aside>
      </div>

      {showRevisions && <RevisionsModal pageId={page.id} onClose={() => setShowRevisions(false)} onRestore={() => { load(); setShowRevisions(false); }} />}
      {showSettings && <PageSettingsModal page={page} onClose={() => setShowSettings(false)} onSaved={(next) => { setPage(next); setShowSettings(false); }} />}
    </div>
  );
}

// =================================================================
// New Page Dialog
// =================================================================
function NewPageDialog({ onCreated }) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [route, setRoute] = useState("");
  const nav = useNavigate();
  const create = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    try {
      const { data } = await api.post("/admin/pages", { title, slug: slug || undefined, route: route || undefined });
      toast.success("Page created");
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not create page");
    }
  };
  return (
    <div className="max-w-lg mx-auto px-4 py-16" data-testid="new-page-dialog">
      <Link to="/admin/website" className="text-xs text-[#165a71] font-medium">← Pages</Link>
      <h1 className="mt-2 font-display text-3xl font-semibold text-[#071b22]">Create a new page</h1>
      <div className="mt-8 space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#252A34] mb-1">Title</label>
          <input value={title} onChange={e => { setTitle(e.target.value); if (!slug) setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")); }} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#165a71] focus:outline-none" data-testid="new-page-title" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#252A34] mb-1">Slug</label>
          <input value={slug} onChange={e => setSlug(e.target.value)} placeholder="hello-world" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#165a71] focus:outline-none" data-testid="new-page-slug" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#252A34] mb-1">Route <span className="text-[#252A34]/50">(defaults to /slug)</span></label>
          <input value={route} onChange={e => setRoute(e.target.value)} placeholder="/hello-world" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#165a71] focus:outline-none" />
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <button onClick={() => nav("/admin/website")} className="rounded-full border border-slate-200 bg-white text-[#252A34] px-4 py-2 text-sm font-medium">Cancel</button>
          <button onClick={create} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]" data-testid="new-page-create">Create</button>
        </div>
      </div>
    </div>
  );
}

// =================================================================
// Sortable section wrapper
// =================================================================
function SortableSection({ section, selectedId, setSelectedId, onMoveUp, onMoveDown, onDuplicate, onRemove, onToggleVisibility, onBlockMoveUp, onBlockMoveDown, onBlockRemove, idx, total }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const selected = selectedId === section.id || section.blocks?.some(b => b.id === selectedId);

  return (
    <div ref={setNodeRef} style={style} className={cx("relative group mb-4 rounded-2xl border-2 bg-white transition", selected ? "border-[#165a71]/50" : "border-slate-200 hover:border-[#165a71]/30")} data-testid={`sec-wrap-${section.id}`}>
      {/* Section toolbar */}
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 px-3 py-1.5 rounded-t-2xl">
        <button
          className="flex items-center gap-2 text-xs text-[#252A34]/70 cursor-grab active:cursor-grabbing"
          {...attributes} {...listeners}
          onClick={() => setSelectedId(section.id)}
          data-testid={`section-handle-${section.id}`}
        >
          <span className="inline-block h-3 w-3 rounded-sm bg-slate-300" />
          <span className="font-medium">{section.name || "Section"}</span>
          {section.hidden && <span className="text-[10px] uppercase tracking-widest text-amber-600">hidden</span>}
        </button>
        <div className="flex items-center gap-0.5">
          <button onClick={onMoveUp} disabled={idx === 0} className="rounded p-1 text-slate-500 hover:bg-white disabled:opacity-40" title="Move up" data-testid={`sec-up-${section.id}`}><ArrowUp className="h-3 w-3" /></button>
          <button onClick={onMoveDown} disabled={idx === total - 1} className="rounded p-1 text-slate-500 hover:bg-white disabled:opacity-40" title="Move down" data-testid={`sec-down-${section.id}`}><ArrowDown className="h-3 w-3" /></button>
          <button onClick={onDuplicate} className="rounded p-1 text-slate-500 hover:bg-white" title="Duplicate"><Copy className="h-3 w-3" /></button>
          <button onClick={onToggleVisibility} className="rounded p-1 text-slate-500 hover:bg-white" title={section.hidden ? "Show" : "Hide"} data-testid={`sec-hide-${section.id}`}><Eye className="h-3 w-3" /></button>
          <button onClick={onRemove} className="rounded p-1 text-red-500 hover:bg-red-50" title="Remove section"><Trash2 className="h-3 w-3" /></button>
        </div>
      </div>
      {/* Section blocks */}
      <div className={cx("relative", section.hidden && "opacity-40")}>
        {(section.blocks || []).map((b, bi) => (
          <div key={b.id} className={cx("relative group/blk px-3 py-2 hover:bg-slate-50/50", selectedId === b.id && "bg-[#165a71]/5 outline outline-2 outline-[#165a71]/40")} onClick={() => setSelectedId(b.id)} data-testid={`block-wrap-${b.id}`}>
            <div className="pointer-events-none">
              {BLOCK_REGISTRY[b.kind]?.Component ? React.createElement(BLOCK_REGISTRY[b.kind].Component, { block: b, mode: "edit" }) : <div className="text-xs text-red-600">Unknown block: {b.kind}</div>}
            </div>
            {selectedId === b.id && (
              <div className="absolute top-1 right-1 flex items-center gap-0.5 rounded-lg bg-white border border-slate-200 shadow-md p-0.5 z-10">
                <button onClick={(e) => { e.stopPropagation(); onBlockMoveUp(b.id); }} disabled={bi === 0} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-40" title="Move up" data-testid={`blk-up-${b.id}`}><ArrowUp className="h-3 w-3" /></button>
                <button onClick={(e) => { e.stopPropagation(); onBlockMoveDown(b.id); }} disabled={bi === (section.blocks?.length || 0) - 1} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-40" title="Move down" data-testid={`blk-down-${b.id}`}><ArrowDown className="h-3 w-3" /></button>
                {isBlockProtected(b) && <span title="Protected system block" className="rounded p-1 text-amber-600"><Lock className="h-3 w-3" /></span>}
                <button onClick={(e) => { e.stopPropagation(); onBlockRemove(b.id); }} className="rounded p-1 text-red-500 hover:bg-red-50" title="Remove"><Trash2 className="h-3 w-3" /></button>
              </div>
            )}
          </div>
        ))}
        {(section.blocks || []).length === 0 && (
          <div className="p-8 text-center text-xs text-[#252A34]/50">Empty section — pick a block from the left sidebar to add here.</div>
        )}
      </div>
    </div>
  );
}

// =================================================================
// Settings panels
// =================================================================
function BlockSettingsPanel({ entry, onChange, device }) {
  const { block } = entry;
  const cfg = BLOCK_REGISTRY[block.kind];
  const label = cfg?.label || block.kind;
  const props = block.props || {};
  const setProp = (k, v) => onChange({ props: { [k]: v } });
  return (
    <div data-testid="block-settings-panel">
      <div className="mb-3">
        <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Block</div>
        <div className="font-display font-semibold text-[#071b22]">{label}</div>
        {isBlockProtected(block) && <div className="mt-1 inline-flex items-center gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"><Lock className="h-3 w-3" />Protected system block</div>}
      </div>
      {/* Universal simple settings — kind-specific keys shown in a generic form */}
      {Object.entries(props).map(([k, v]) => (
        <PropField key={k} label={k} value={v} onChange={(nv) => setProp(k, nv)} />
      ))}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="text-xs font-semibold text-[#071b22] mb-1.5">Responsive · {device}</div>
        <label className="flex items-center gap-2 text-xs text-[#252A34]/80">
          <input type="checkbox" checked={!!(block.responsive?.[device]?.hidden)} onChange={(e) => onChange({ responsive: { ...(block.responsive || {}), [device]: { ...(block.responsive?.[device] || {}), hidden: e.target.checked } } })} data-testid={`resp-hidden-${device}`} />
          Hide on {device}
        </label>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="text-xs font-semibold text-[#071b22] mb-1.5">Visibility</div>
        <select value={block.visibility || "all"} onChange={(e) => onChange({ visibility: e.target.value })} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs" data-testid="block-visibility">
          <option value="all">Everyone</option>
          <option value="visitors">Visitors (logged out)</option>
          <option value="authenticated">Logged in</option>
          <option value="customers">Customers</option>
          <option value="providers">Providers</option>
        </select>
      </div>
    </div>
  );
}

function SectionSettingsPanel({ section, onChange }) {
  return (
    <div data-testid="section-settings-panel">
      <div className="mb-3">
        <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Section</div>
        <input value={section.name || ""} onChange={(e) => onChange({ name: e.target.value })} className="w-full font-display font-semibold text-[#071b22] rounded-md border border-slate-200 px-2 py-1.5 mt-1" placeholder="Section name" data-testid="section-name" />
      </div>
      <div className="mb-4">
        <div className="text-xs font-semibold text-[#071b22] mb-1.5">Layout</div>
        <select value={section.layout || "container"} onChange={(e) => onChange({ layout: e.target.value })} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs" data-testid="section-layout">
          <option value="container">Container (full width row)</option>
          <option value="two-column">Two columns (2fr / 1fr)</option>
        </select>
      </div>
      <div className="mb-4">
        <label className="flex items-center gap-2 text-xs text-[#252A34]/80">
          <input type="checkbox" checked={!!section.hidden} onChange={(e) => onChange({ hidden: e.target.checked })} />
          Hide section
        </label>
      </div>
    </div>
  );
}

function PropField({ label, value, onChange }) {
  const isMultiline = typeof value === "string" && value.length > 80;
  const isBool = typeof value === "boolean";
  const isNumber = typeof value === "number";
  const isObject = value !== null && typeof value === "object";
  return (
    <div className="mb-3">
      <label className="block text-[11px] font-medium text-[#252A34] mb-1 capitalize">{label.replace(/_/g, " ")}</label>
      {isBool
        ? <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} data-testid={`prop-${label}`} />
        : isNumber
        ? <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs" data-testid={`prop-${label}`} />
        : isMultiline
        ? <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs" data-testid={`prop-${label}`} />
        : isObject
        ? <textarea value={JSON.stringify(value, null, 2)} onChange={(e) => { try { onChange(JSON.parse(e.target.value)); } catch {} }} rows={4} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-[10px] font-mono" data-testid={`prop-${label}`} />
        : <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs" data-testid={`prop-${label}`} />}
    </div>
  );
}

// =================================================================
// Page Settings modal
// =================================================================
function PageSettingsModal({ page, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: page.title,
    slug: page.slug,
    seo_title: page.seo_title || "",
    seo_description: page.seo_description || "",
    seo_keywords: (page.seo_keywords || []).join(", "),
    canonical_url: page.canonical_url || "",
    robots_index: page.robots_index !== false,
    robots_follow: page.robots_follow !== false,
    login_required: !!page.login_required,
    access_role: page.access_role || "",
    visibility: page.visibility || "public",
    custom_css: page.custom_css || "",
    admin_notes: page.admin_notes || "",
    review_by_counsel: !!page.review_by_counsel,
  });
  const save = async () => {
    const payload = { ...form, seo_keywords: form.seo_keywords.split(",").map(s => s.trim()).filter(Boolean) };
    const { data } = await api.put(`/admin/pages/${page.id}`, payload);
    toast.success("Settings saved");
    onSaved(data);
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="page-settings-modal">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display font-semibold text-[#071b22]">Page settings — {page.title}</div>
          <button onClick={onClose} className="text-slate-500 hover:text-[#252A34]">✕</button>
        </header>
        <div className="p-6 overflow-auto space-y-4">
          <Field label="Title"><input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></Field>
          {!page.is_system && !page.is_dashboard && (
            <Field label="Slug"><input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></Field>
          )}
          <div className="pt-2 text-xs font-semibold text-[#071b22] uppercase tracking-wider">SEO</div>
          <Field label="SEO title"><input value={form.seo_title} onChange={e => setForm({ ...form, seo_title: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="settings-seo-title" /></Field>
          <Field label="Meta description"><textarea rows={3} value={form.seo_description} onChange={e => setForm({ ...form, seo_description: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="settings-seo-description" /></Field>
          <Field label="Keywords (comma-separated)"><input value={form.seo_keywords} onChange={e => setForm({ ...form, seo_keywords: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></Field>
          <Field label="Canonical URL"><input value={form.canonical_url} onChange={e => setForm({ ...form, canonical_url: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" placeholder="https://..." /></Field>
          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.robots_index} onChange={e => setForm({ ...form, robots_index: e.target.checked })} data-testid="robots-index" />Index by search engines</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.robots_follow} onChange={e => setForm({ ...form, robots_follow: e.target.checked })} />Follow links</label>
          </div>
          <div className="pt-2 text-xs font-semibold text-[#071b22] uppercase tracking-wider">Access</div>
          <Field label="Visibility">
            <select value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
              <option value="public">Public</option>
              <option value="private">Private (admin-only)</option>
              <option value="password">Password-protected</option>
            </select>
          </Field>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.login_required} onChange={e => setForm({ ...form, login_required: e.target.checked })} />Requires login</label>
          </div>
          <Field label="Required role (optional)"><input value={form.access_role} onChange={e => setForm({ ...form, access_role: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" placeholder="e.g. administrator" /></Field>
          <div className="pt-2 text-xs font-semibold text-[#071b22] uppercase tracking-wider">Advanced</div>
          <Field label="Custom CSS (page-scoped)"><textarea rows={4} value={form.custom_css} onChange={e => setForm({ ...form, custom_css: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-xs font-mono" /></Field>
          <Field label="Admin notes (never public)"><textarea rows={2} value={form.admin_notes} onChange={e => setForm({ ...form, admin_notes: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></Field>
        </div>
        <footer className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]" data-testid="settings-save">Save</button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#252A34] mb-1">{label}</label>
      {children}
    </div>
  );
}

// =================================================================
// Revisions modal
// =================================================================
function RevisionsModal({ pageId, onClose, onRestore }) {
  const [revs, setRevs] = useState([]);
  useEffect(() => { api.get(`/admin/pages/${pageId}/revisions`).then(r => setRevs(r.data || [])); }, [pageId]);
  const restore = async (rid) => {
    await api.post(`/admin/pages/${pageId}/revisions/${rid}/restore`);
    toast.success("Revision restored to draft — click Publish to make it live.");
    onRestore();
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="revisions-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden max-h-[80vh] flex flex-col">
        <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display font-semibold text-[#071b22]">Revision history</div>
          <button onClick={onClose} className="text-slate-500 hover:text-[#252A34]">✕</button>
        </header>
        <div className="px-6 py-3 border-b border-slate-100 bg-amber-50/60 text-xs text-[#252A34]/80 flex items-start gap-2" data-testid="restore-hint">
          <span className="shrink-0 mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">i</span>
          <span>Restoring loads the revision into the <strong>draft</strong> — click <strong>Publish</strong> to make it live.</span>
        </div>
        <div className="p-4 overflow-auto">
          {revs.length === 0 && <div className="text-sm text-[#252A34]/60 text-center py-8">No revisions yet.</div>}
          <ul className="divide-y divide-slate-100">
            {revs.map(r => (
              <li key={r.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium text-[#071b22]">Version {r.version} · {r.label || "auto"}</div>
                  <div className="text-xs text-[#252A34]/60">{r.editor_name || r.editor_id} · {(r.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <button onClick={() => restore(r.id)} className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-[#165a71] hover:border-[#165a71]/40" data-testid={`restore-${r.id}`}>Restore</button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// =================================================================
// Helpers
// =================================================================
function groupedBlocks() {
  const g = {};
  Object.entries(BLOCK_REGISTRY).forEach(([key, cfg]) => {
    const grp = cfg.group || "basic";
    (g[grp] = g[grp] || []).push({ key, label: cfg.label });
  });
  return g;
}
